import React, { useState } from 'react';
import {
  DateRangePicker,
  FocusedInputShape,
  SingleDatePicker,
} from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import { ConversationCheckAvailability } from '@tomis-tech/types';

import {
  Overlay,
  ErrorMessage,
  Button,
  ButtonsContainer,
  ScrollContainer,
  TitleText,
} from './styled-components';
import { SavingSpinner } from './common';
import { useCheckAvailability, useSession, useParent } from '../context';
import { createCheckAvailabilityUserActionMessage } from '../messages';
import { abbreviatedDateRange } from '../utils';
import { updateConversationCheckAvailability } from '../services/checkAvailability';
import {
  CategoryButton,
  CategoriesContainer,
} from './CheckAvailabilityProduct';
import { ErrorBoundary } from './ErrorBoundary';

const CheckAvailabilityDates: React.FC = () => {
  const {
    changingDates,
    setChangingDates,
    setHeaderErrorMessage,
  } = useCheckAvailability();
  const { conversation } = useSession();
  const { pageView } = useParent();

  // guaranteed to exist on mount
  const { checkAvailability } = conversation.value;

  const [startDate, setStartDate] = useState(
    moment(checkAvailability.dateTimeStart || new Date())
  );
  const [endDate, setEndDate] = useState(
    moment(checkAvailability.dateTimeEnd || new Date())
  );
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape>(
    'startDate'
  );
  const [isSingleDateFocused, setIsSingleDateFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dateRangeMode, setDateRangeMode] = useState<
    'multi' | 'single' | 'none'
  >('none');

  /** If a new date range has been selected (must be both start and end),
   * update the Check Availability parameters on the Conversation document */
  async function saveNewDateRange(start: Date, end: Date) {
    setIsSaving(true);
    setErrorMessage('');
    setHeaderErrorMessage('');

    const updatedCheckAvailability: ConversationCheckAvailability = {
      ...checkAvailability,
      dateTimeStart: start,
      dateTimeEnd: end,
    };

    const message = createCheckAvailabilityUserActionMessage(
      `Dates: ${abbreviatedDateRange(start, end)}`,
      checkAvailability,
      pageView
    );

    /**
     * 1. Save Check Availability parameters on Conversation document
     * 2. Add a System Message to Messages collection
     * 3. Close the parameter selection overlay on success
     * 4. Error handling (Sentry & error messages for users)
     */
    const { error } = await updateConversationCheckAvailability({
      conversation,
      message,
      updatedCheckAvailability,
    });
    setIsSaving(false);
    setChangingDates(false);

    if (error) {
      setErrorMessage('Error saving date');
    }
  }

  if (!changingDates) return null;

  const isSmallScreen = window.innerWidth < 350 || window.innerHeight < 600;

  return (
    <ErrorBoundary
      errorUI={
        <ErrorMessage>
          Unexpected error occurred when selecting dates. Try closing and
          reopening the chat widget.
        </ErrorMessage>
      }
    >
      <Overlay>
        <ScrollContainer style={{ overflow: 'visible' }}>
          {dateRangeMode === 'none' ? (
            <>
              <TitleText>
                Would you like to check availability for a single day, or date
                range?
              </TitleText>
              <CategoriesContainer>
                <CategoryButton
                  aria-checked={false}
                  onClick={() => {
                    setDateRangeMode('single');
                    setIsSingleDateFocused(true);
                  }}
                >
                  Single Day
                </CategoryButton>
                <CategoryButton
                  aria-checked={false}
                  onClick={() => setDateRangeMode('multi')}
                >
                  Date Range
                </CategoryButton>
              </CategoriesContainer>
            </>
          ) : (
            <>
              <TitleText>
                {dateRangeMode === 'multi'
                  ? 'Select a date range'
                  : 'Select a date'}
              </TitleText>
              {dateRangeMode === 'multi' && (
                <DateRangePicker
                  block
                  minimumNights={0}
                  endDate={endDate}
                  endDateId="endDateId"
                  hideKeyboardShortcutsPanel={true}
                  focusedInput={focusedInput}
                  /** Limit date range to 30 days to simplify integrations
                   * with reservations systems on the backend */
                  isDayBlocked={day => {
                    if (startDate && focusedInput === 'endDate') {
                      return day.isAfter(startDate.clone().add(30, 'days'));
                    } else {
                      return false;
                    }
                  }}
                  numberOfMonths={1}
                  onDatesChange={({
                    startDate,
                    endDate,
                  }: {
                    startDate: moment.Moment | null;
                    endDate: moment.Moment | null;
                  }) => {
                    setEndDate(endDate);
                    setStartDate(startDate);

                    if (focusedInput === 'endDate' && startDate && endDate) {
                      saveNewDateRange(startDate.toDate(), endDate.toDate());
                    }
                  }}
                  orientation={isSmallScreen ? 'vertical' : 'horizontal'}
                  onClose={() => setDateRangeMode('none')}
                  onFocusChange={focusedInput => {
                    setFocusedInput(focusedInput);
                  }}
                  startDateId="startDateId"
                  startDate={startDate}
                  withFullScreenPortal={isSmallScreen ? true : false}
                />
              )}
              {dateRangeMode === 'single' && (
                <SingleDatePicker
                  block
                  date={startDate}
                  focused={isSingleDateFocused}
                  hideKeyboardShortcutsPanel={true}
                  id="1"
                  numberOfMonths={1}
                  orientation={isSmallScreen ? 'vertical' : 'horizontal'}
                  onClose={() => setDateRangeMode('none')}
                  onDateChange={date => {
                    setStartDate(date);
                    setEndDate(date);
                    if (date) {
                      saveNewDateRange(date.toDate(), date.toDate());
                    }
                  }}
                  onFocusChange={({ focused }) => {
                    setIsSingleDateFocused(focused);
                  }}
                  withFullScreenPortal={isSmallScreen ? true : false}
                />
              )}
            </>
          )}
        </ScrollContainer>
        <ButtonsContainer
          style={{ flexDirection: 'column-reverse', paddingBottom: '20px' }}
        >
          <Button
            color="gray"
            disabled={isSaving}
            onClick={() => setChangingDates(false)}
          >
            {isSaving ? <SavingSpinner /> : 'CANCEL'}
          </Button>
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </ButtonsContainer>
      </Overlay>
    </ErrorBoundary>
  );
};

export default CheckAvailabilityDates;
